import React from "react";

import { useTranslation } from "utils";

import { CurrencyCode } from "consts";

import { SingleFieldOutcomes } from "models";

import { Flex, Typography } from "components";

import { PayoutTimelineBarChart } from "pages/app/field-timeline/components/payout-context-drawer/PayoutTimelineBarChart";
import { buildFiveYearMonitoringWindow, hasDataEntryForYear } from "pages/app/field-timeline/utils/PayoutUtils";

export type PayoutTimelineRow = {
  year: number;
  actual: number;
  eligible: number;
  nonEligible: number;
  unknown: boolean;
  isVerified: boolean;
};

type PayoutContextTimelineCardProps = {
  currency?: CurrencyCode;
  actualPayout: number;
  eligiblePayout: number;
  allFieldOutcomes: SingleFieldOutcomes[];
  farmPayments: { year: number; status: "paid" | "pending" | "inVerification"; amount: number }[];
  totalPayoutMax: number;
  reportingYear: number;
  monitoringYears: number[];
};

export const PayoutContextTimelineCard = ({
  currency,
  actualPayout,
  eligiblePayout,
  allFieldOutcomes,
  totalPayoutMax,
  farmPayments,
  reportingYear,
  monitoringYears,
}: PayoutContextTimelineCardProps) => {
  const { t } = useTranslation("fieldTimeline");
  const firstMonitoringYear = monitoringYears[0] ?? reportingYear;
  const timelineYears = buildFiveYearMonitoringWindow(firstMonitoringYear);

  const timelineRows = timelineYears.map((year) => {
    const hasYearData = hasDataEntryForYear(allFieldOutcomes, year, true);
    const isPastYear = year < reportingYear;
    const isCurrentYear = year === reportingYear;
    const isVerified = farmPayments.some((payment) => payment.year === year && payment.status === "paid");

    const getActualPayoutForYear = (year: number) => {
      const pastPayout = farmPayments.find((payment) => payment.year === year)?.amount ?? 0;
      if (isPastYear) return pastPayout;
      if (isCurrentYear) return actualPayout;
      return null;
    };

    return {
      year,
      actual: getActualPayoutForYear(year),
      eligible: isCurrentYear ? eligiblePayout : 0,
      nonEligible: hasYearData && !isPastYear ? totalPayoutMax : 0,
      unknown: !hasYearData && !isVerified && isPastYear,
      isVerified,
    };
  });

  const legendItems = [
    {
      className: "actual",
      label: t("impactPanel.payoutImpact.contextDrawer.timeline.actual"),
    },
    {
      className: "eligible",
      label: t("impactPanel.payoutImpact.contextDrawer.timeline.eligible"),
    },
    {
      className: "non-eligible",
      label: t("impactPanel.payoutImpact.contextDrawer.timeline.nonEligible"),
    },
    {
      className: "unknown",
      label: t("impactPanel.payoutImpact.contextDrawer.timeline.unknown"),
      value: "",
    },
  ];

  return (
    <Flex vertical className="pcd-card">
      <Typography variant="paragraph-l" className="pcd-timeline-title">
        {t("impactPanel.payoutImpact.contextDrawer.timeline.title")}
      </Typography>
      <div className="pcd-timeline-chart">
        <PayoutTimelineBarChart rows={timelineRows} currency={currency} />
      </div>
      <Flex className="pcd-timeline-legend margin-top-50 margin-left-300" gap="1rem" style={{ rowGap: "0.5rem", columnGap: "1rem" }}>
        {legendItems.map((item) => (
          <Flex key={`${item.className}-${item.label}`} gap="0.375rem">
            <i className={item.className} />
            <Flex vertical className="pcd-legend-copy" gap="0.125rem">
              <Typography variant="paragraph-s" className="pcd-legend-label">
                {item.label}
              </Typography>
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};
